import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type PricingMode = 'net_profit' | 'market_only';

interface Ctx {
  mode: PricingMode;
  setMode: (m: PricingMode) => void;
}

const KEY = 'aurum_pricing_mode_v1';
const PricingModeContext = createContext<Ctx | null>(null);

export function PricingModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<PricingMode>('net_profit');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(KEY) as PricingMode | null;
      if (saved === 'net_profit' || saved === 'market_only') setModeState(saved);
    } catch { /* noop */ }
  }, []);

  const setMode = (m: PricingMode) => {
    setModeState(m);
    try { localStorage.setItem(KEY, m); } catch { /* noop */ }
  };

  return (
    <PricingModeContext.Provider value={{ mode, setMode }}>
      {children}
    </PricingModeContext.Provider>
  );
}

export function usePricingMode() {
  const ctx = useContext(PricingModeContext);
  if (!ctx) throw new Error('usePricingMode must be used inside PricingModeProvider');
  return ctx;
}

export const PRICING_MODE_LABELS: Record<PricingMode, { short: string; full: string; desc: string }> = {
  net_profit: {
    short: 'Net Profit',
    full: 'Net Profit Mode',
    desc: 'Compare purchase price vs current market price. Shows profit, ROI, and gains.',
  },
  market_only: {
    short: 'Market Only',
    full: 'Market Value Only',
    desc: 'Reporting view: show only current live market valuations. Hides purchase costs and profit.',
  },
};
